'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

import { FundingTypeEnum } from '@flipster/apis/v2/snapshot';
import { FundingTypeTextMapper } from '@flipster/apis/v2/snapshot/constants';
import { ProductTypeBadge, SymbolMaxLeverageBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { recentHistoryBunja } from '@flipster/features/src/recent-history/state';
import { FavoriteSymbolButton } from '@flipster/features/src/shared/favorite-symbol/button';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { isTradFiInstrument } from '@flipster/features/src/shared/rwa';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { TableCell, TableRow } from '@flipster/ui/Table';
import { FundingRatePrecision } from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { marketPageBunja, marketTabListBunja } from '../../../state';
import { TabValueType, TableItem } from '../../../types';

interface Props {
  tab: TabValueType;
  item: TableItem;
  isSearching: boolean;
}

export const DesktopRow = ({ tab, item, isSearching }: Props) => {
  const router = useRouter();

  const { searchCursorAtom } = useBunja(marketPageBunja);
  const { query: search } = useAtomValue(searchCursorAtom);
  const isSearchMode = Boolean(search);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { tabListAtom } = useBunja(marketTabListBunja);
  const { addRecentSearchHistory } = useBunja(recentHistoryBunja);

  const tabList = useAtomValue(tabListAtom);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  const instrument = instrumentsKeyBySymbol?.[item.symbol];

  const badge = useMemo(() => {
    if (!instrument) return null;

    if (!instrument.isSpot)
      return (
        <SymbolMaxLeverageBadge
          crossMax={instrument.maxLeverageHardLimit}
          isolatedCap={instrument.maxInitLeverageIsolatedMargin}
        />
      );
    return <ProductTypeBadge productType='spot' />;
  }, [instrument]);

  const onClickRow = () => {
    addRecentSearchHistory(item.symbol);
    setOrderAttribution(
      isSearchMode ? OrderAttributionMapper.market.search : OrderAttributionMapper.market.discover[tab],
    );

    if (instrument?.isSpot) {
      router.push(`/trade/spot/${item.symbol}`);
    } else {
      router.push(`/trade/perpetual/${item.symbol}`);
    }

    const currentTab = tabList.find((t) => t.value === tab);
    if (!currentTab) return;
    segment.track(TradeEvent.TradeCryptoClick, {
      trade_type: instrument?.isSpot ? 'spot' : 'perpetual',
      carousel_type: currentTab.value,
      symbol: item.symbol,
      price_change_percentage: Number(numberFormatter(item.priceChangePct24h, { precision: 2 })),
      event_location: 'discover_tab',
    });
  };

  if (!instrument) return null;

  return (
    <TableRow
      key={`${tab}_${item.symbol}`}
      onClick={onClickRow}
      className={css({
        '&:last-child > td': {
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderColor: 'border.default.level-1',
        },
      })}
    >
      <TableCell align='left'>
        {tab === 'favorites' && !isSearchMode && <FavoriteSymbolButton symbol={item.symbol} />}
        <SymbolDisplay
          symbol={item.symbol}
          icon={<SymbolDisplay.Icon size={32} space={12} />}
          mainLabel={
            <div className={hstack({ gap: 4 })}>
              <SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />
              {badge}
            </div>
          }
          subLabel={
            isTradFiInstrument(instrument) ? (
              <span
                className={css({
                  display: 'block',
                  textStyle: 'Caption/L/Monospace/Regular',
                  color: 'content.accent.promotion',
                  whiteSpace: 'nowrap',
                })}
              >
                {instrument.displayName}
              </span>
            ) : undefined
          }
        />
      </TableCell>
      {tab === 'absFundingRates' && !isSearching && (
        <TableCell align='right'>
          <Formatted.Percent
            value={item.fundingRate}
            precision={FundingRatePrecision}
            withColor
            withPlus
            isRate
            toFixed
            css={{ textStyle: 'Body/M/Monospace/Regular' }}
          />
          <span
            className={css({
              textStyle: 'Text/M/Regular',
              pl: 2,
              color: 'content.default.level-1',
            })}
          >{`/${FundingTypeTextMapper[instrument.fundingType ?? FundingTypeEnum.ImmediateHour8]}`}</span>
        </TableCell>
      )}
      <TableCell align='right'>
        <Formatted.ShortNumber
          value={item.turnover24h}
          precision={2}
          toFixed
          css={{ textStyle: 'Body/M/Monospace/Regular' }}
        />
      </TableCell>
      <TableCell align='right'>
        <Formatted.Number
          method='round-half-up'
          value={item.displayPrice}
          precision={instrument.pricePrecision}
          toFixed
          css={{ textStyle: 'Body/M/Monospace/Regular' }}
        />
      </TableCell>
      <TableCell align='right'>
        <Formatted.Percent
          value={item.priceChangePct24h}
          precision={2}
          withColor
          withArrow
          toFixed
          css={{ textStyle: 'Body/M/Monospace/Regular' }}
        />
      </TableCell>
      <TableCell align='right'>
        <Button
          hierarchy='secondary'
          color='accent'
          size='medium'
          label={<FormattedMessage id='Common.r250401.Trade' />}
          as='a'
          href={instrument.isSpot ? `/trade/spot/${item.symbol}` : `/trade/perpetual/${item.symbol}`}
          onClick={(e) => {
            e.stopPropagation();
            setOrderAttribution(
              isSearchMode ? OrderAttributionMapper.market.search : OrderAttributionMapper.market.discover[tab],
            );
            addRecentSearchHistory(item.symbol);
          }}
        />
      </TableCell>
    </TableRow>
  );
};
